import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { 
  Shield, 
  Users, 
  MessageSquare, 
  CheckSquare,
  ArrowRight,
  AlertCircle,
  TrendingUp,
  Database,
  Upload
} from 'lucide-react';
import { useAdminStats } from '@/hooks/useAdminStats';
import { Skeleton } from '@/components/ui/skeleton';

export const AdminProfileSection: React.FC = () => {
  const navigate = useNavigate();
  const { pendingClaims, totalFeedback, activeUsers, loading, error } = useAdminStats();

  if (loading) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle>Admin Controls</CardTitle>
            <Badge variant="default" className="ml-auto">Admin</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle>Admin Controls</CardTitle>
          <Badge variant="default" className="ml-auto">Admin</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-background">
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-foreground">{pendingClaims}</div>
              <div className="text-sm text-muted-foreground">Pending Claims</div>
            </CardContent>
          </Card>

          <Card className="bg-background">
            <CardContent className="p-4 text-center">
              <MessageSquare className="h-8 w-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-foreground">{totalFeedback}</div>
              <div className="text-sm text-muted-foreground">Total Feedback</div>
            </CardContent>
          </Card>

          <Card className="bg-background">
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-8 w-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-foreground">{activeUsers}</div>
              <div className="text-sm text-muted-foreground">Active Users</div>
            </CardContent>
          </Card>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button asChild 
            className="w-full justify-between"
            variant="outline"
          >
            <Link to="/admin/users">
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Active Users
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild 
            className="w-full justify-between"
            variant="outline"
          >
            <Link to="/admin/feedback">
              <span className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                View Feedback
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild 
            className="w-full justify-between"
            variant="outline"
          >
            <Link to="/admin/api-sports">
              <span className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                API Sports Management
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild 
            className="w-full justify-between"
            variant="outline"
          >
            <Link to="/admin/import-accounts">
              <span className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Import Accounts
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild 
            className="w-full justify-between"
            variant="outline"
          >
            <Link to="/admin/checklist">
              <span className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4" />
                Pre-Launch Checklist
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
